import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import StaticMapComponent from 'reservations/account/components/StaticMapComponent';
import { useContactInfo } from 'reservations/account/providers/ConfigProvider';
import {
  useAcceptsReservations,
  useAcceptsWaitlist,
  useIsWaitlistOnly
} from 'reservations/account/providers/ConfigProvider';
import Header from 'reservations/base/components/Header';
import { formatPhone } from 'reservations/base/helpers/phone';
import useIsDesktop from 'reservations/base/hooks/useIsDesktop';
import useIsSmallAndUp from 'reservations/base/hooks/useIsSmallAndUp';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { MEDIUM_SOY, MEDIUM_TOFU, WHITE } from 'reservations/brand/constants';

type LocationCardButtonProps = {
  isDesktop: boolean;
  slug: string;
};
interface BaseLocationCardButtonProps extends LocationCardButtonProps {
  label: string;
}

const BaseLocationCardButton = ({
  isDesktop,
  label,
  slug
}: BaseLocationCardButtonProps) => {
  const { handleNavigation } = useCustomRouter();
  const goToLocationsPage = () => {
    handleNavigation(`/locations/${slug}`);
  };
  const reserveButtonStyles = isDesktop ? {} : { width: '100%' };

  return (
    <Grid data-testid="location-card-button-grid" item sx={reserveButtonStyles}>
      <Button
        data-testid="location-card-button"
        onClick={goToLocationsPage}
        variant="contained"
        fullWidth
        sx={{
          height: '56px',
          width: isDesktop ? '150px' : 'inherit',
          textTransform: 'none',
          fontSize: '18px',
          padding: '35px 50px',
          lineHeight: '20px'
        }}
      >
        {label}
      </Button>
    </Grid>
  );
};

const LocationCardButton = ({ isDesktop, slug }: LocationCardButtonProps) => {
  const isAcceptingReservations = useAcceptsReservations();
  const isAcceptingWaitlist = useAcceptsWaitlist();
  const isWaitlistOnly = useIsWaitlistOnly();

  let label;

  switch (true) {
    case isAcceptingWaitlist && isAcceptingReservations:
      label = 'Reservations & Waitlist';
      break;
    case isAcceptingWaitlist && !isAcceptingReservations:
      label = 'Join Waitlist';
      break;
    case isAcceptingReservations && !isAcceptingWaitlist:
      label = 'Reserve';
      break;
    default:
      label = null;
  }

  return label ? (
    <BaseLocationCardButton isDesktop={isDesktop} label={label} slug={slug} />
  ) : (
    <Grid item>
      <Typography
        data-testid="location-card-closed"
        sx={{
          width: isDesktop ? '150px' : 'inherit',
          color: MEDIUM_SOY,
          fontSize: '14px',
          fontWeight: 400,
          fontStyle: 'normal',
          paddingBottom: '2px'
        }}
      >
        {isWaitlistOnly
          ? 'The waitlist is currently closed.'
          : "This location isn't taking bookings right now."}
      </Typography>
    </Grid>
  );
};

type LocationCardProps = {
  name: string;
  phone_number: string;
  street: string;
  state: string;
  postal_code: string;
  google_maps_static_urls: {
    static_url_desktop: string;
    static_url_tablet: string;
    static_url_mobile: string;
  };
  slug: string;
};

const LocationCard = ({
  name,
  street,
  state,
  postal_code: postalCode,
  google_maps_static_urls: staticMapsURLs,
  slug
}: LocationCardProps) => {
  const isDesktop = useIsDesktop();
  const isSmallAndUp = useIsSmallAndUp();
  const { phoneNumber } = useContactInfo();

  const locationInfoStyles = {
    justifyContent: 'center',
    margin: isDesktop ? '16px 0' : '20px'
  };
  const reserveButtonGridStyles = {
    justifyContent: 'center',
    borderLeft: `1px solid ${MEDIUM_TOFU}`,
    flexBasis: 'fit-content',
    margin: isDesktop ? '16px 0' : '0 0 16px'
  };

  return (
    <Card>
      <CardContent sx={{ p: '0 !important' }}>
        <Grid container wrap="nowrap" direction={isDesktop ? 'row' : 'column'}>
          <Grid
            container
            item
            direction="column"
            sx={{
              mr: '20px',
              flexBasis: 'fit-content'
            }}
          >
            <Grid item>
              <StaticMapComponent
                name={name}
                isMediumAndUp={isDesktop}
                isSmallAndUp={isSmallAndUp}
                staticUrls={staticMapsURLs}
              />
            </Grid>
          </Grid>
          <Grid container item direction="column" sx={locationInfoStyles}>
            <Grid item>
              <Header text={name} />
            </Grid>
            <Grid item>
              <Typography
                sx={{ color: WHITE }}
                data-testid="location-card-address"
              >{`${street} ${state} ${postalCode}`}</Typography>
            </Grid>
            {phoneNumber && (
              <Grid item>
                <Typography
                  sx={{ color: WHITE }}
                  data-testid="location-card-phone-number"
                >
                  {formatPhone(phoneNumber)}
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item direction="column" sx={reserveButtonGridStyles}>
            <CardActions sx={{ p: '0 20px' }}>
              <Grid container>
                <LocationCardButton isDesktop={isDesktop} slug={slug} />
              </Grid>
            </CardActions>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default LocationCard;
